/** @jsx jsx */
import React, { useCallback, useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import { getATLContextUrl } from '@atlaskit/atlassian-context';
import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { IssuesByType } from '@atlassian/jira-business-summary-services/src/services/types.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { Link } from '@atlassian/react-resource-router';
import { useSectionAnalytics } from '../../../common/ui/analytics/index.tsx';
import { ChartWithHeaderContainer } from '../../../common/ui/chart-with-header-container/index.tsx';
import {
	PercentageTableRow,
	SinglePercentageTable,
	type SinglePercentageTableRenderRowArgs,
} from '../../../common/ui/single-percentage-table/index.tsx';
import { useSummaryFeatures } from '../../../controllers/features-context/index.tsx';
import type { PaginatedDataProps } from '../../avp-bridge/with-paginated-data/index.ts';
import {
	BoundaryIds,
	MetricTypes,
	SectionIds,
	type ColorScheme,
	type MetricType,
} from '../../../common/types.tsx';
import messages from './messages.tsx';

const IssueTypeRow = ({
	item: {
		issueType: { id, name, iconUrl },
	},
	totalUnresolvedIssues,
	counterText,
	linksResolver,
	linksTarget,
	metric,
	count,
	colorScheme,
}: SinglePercentageTableRenderRowArgs<IssuesByType>) => {
	const { fireSectionAnalytics } = useSectionAnalytics(
		SectionIds.TypesOfWork,
		BoundaryIds.TypesOfWork,
	);

	const onRowClick = () => {
		fireSectionAnalytics('viewWithFilter', { count });
	};
	const onClickHref = linksResolver({ filter: `type = '${id}'`, hideDone: true });

	return (
		<PercentageTableRow
			count={count}
			totalUnresolvedIssues={totalUnresolvedIssues}
			counterText={counterText}
			onClickHref={onClickHref}
			linksTarget={linksTarget}
			onClick={onRowClick}
			linkAriaLabel={name}
			metric={metric}
			colorScheme={colorScheme}
		>
			<img alt="" src={iconUrl} css={iconStyles} />
			<Text maxLines={1}>{name}</Text>
		</PercentageTableRow>
	);
};

export const TypesOfWork = ({
	metricType = MetricTypes.DISTRIBUTION,
	...props
}: PaginatedDataProps<IssuesByType> & { metricType?: MetricType; colorScheme?: ColorScheme }) => {
	const selector = useCallback((item: IssuesByType) => item.count, []);

	const typesOfWorkTotal = useMemo(
		() => props.data.reduce((acc, { count }) => acc + count, 0),
		[props.data],
	);

	const { linksResolver, linksTarget } = useSummaryFeatures();
	const { formatMessage } = useIntl();
	const { fireSectionAnalytics } = useSectionAnalytics(
		SectionIds.TypesOfWork,
		BoundaryIds.TypesOfWork,
	);

	const onLinkClick = useCallback(() => {
		fireSectionAnalytics('view');
	}, [fireSectionAnalytics]);

	return (
		<ChartWithHeaderContainer
			header={
				<>
					<Heading size="small" as="h2">
						<FormattedMessage {...messages.title} />
					</Heading>
					{typesOfWorkTotal ? (
						<Text>
							<FormattedMessage
								{...messages.description}
								values={{
									link: (chunks) => (
										<Link
											href={linksResolver({
												sortBy: 'issueType',
												hideDone: true,
											})}
											target={linksTarget}
											onClick={onLinkClick}
										>
											{chunks}
										</Link>
									),
								}}
							/>
						</Text>
					) : (
						<Text>
							<FormattedMessage
								{...messages.emptyDescription}
								values={{
									link: (chunks) => (
										<Link
											href={`${getATLContextUrl('support')}/jira-cloud-administration/docs/what-are-issue-types/`}
											target="_blank"
										>
											{chunks}
										</Link>
									),
								}}
							/>
						</Text>
					)}
				</>
			}
			chart={
				<SinglePercentageTable
					typeText={formatMessage(messages.typeColumn)}
					distributionText={formatMessage(messages.distributionColumn)}
					counterText={formatMessage(messages.counter)}
					RowComponent={IssueTypeRow}
					getKey={({ issueType }) => issueType.id}
					countSelector={selector}
					metricType={metricType}
					{...props}
				/>
			}
		/>
	);
};

const iconStyles = css({
	width: token('space.200', '16px'),
	height: token('space.200', '16px'),
});
